/**
 * The staff permission matrix (S4), client side. The server is the authority (`StaffCapability`);
 * this only decides which buttons to draw, so a tier never sees a control it cannot use. Keep the
 * table in step with `StaffCapability.java`; `tier.test.ts` pins every row.
 */

export type StaffTier = 'support' | 'operator' | 'superoperator' | 'superadmin'

export const STAFF_TIERS: readonly StaffTier[] = ['support', 'operator', 'superoperator', 'superadmin']

export type Capability =
  | 'directory_read'
  | 'feedback_triage'
  | 'suspend_reactivate'
  | 'zone_identity_ops'
  | 'deletion_cancel_verify'
  | 'billing_plan_quota'
  | 'invoice_resend'
  | 'deletion_expedite'
  | 'break_glass'
  | 'impersonation'
  | 'staff_roles'
  | 'audit_read'
  | 'feedback_delete'

/** The lowest tier holding each capability; every higher tier holds it too (S3). */
export const CAPABILITY_MINIMUM: Readonly<Record<Capability, StaffTier>> = {
  directory_read: 'support',
  feedback_triage: 'support',
  suspend_reactivate: 'operator',
  zone_identity_ops: 'operator',
  deletion_cancel_verify: 'operator',
  billing_plan_quota: 'superoperator',
  invoice_resend: 'operator',
  deletion_expedite: 'superoperator',
  break_glass: 'superoperator',
  impersonation: 'superoperator',
  staff_roles: 'superadmin',
  audit_read: 'superadmin',
  feedback_delete: 'superadmin',
}

export function parseTier(value: string | null | undefined): StaffTier | null {
  return STAFF_TIERS.find((tier) => tier === value) ?? null
}

export function tierRank(tier: StaffTier | null | undefined): number {
  return tier === null || tier === undefined ? -1 : STAFF_TIERS.indexOf(tier)
}

export function canDo(tier: StaffTier | null | undefined, capability: Capability): boolean {
  return tierRank(tier) >= tierRank(CAPABILITY_MINIMUM[capability])
}

/** Reason lengths, mirroring `StaffReasons` (10 for writes, 20 for break-glass, at most 2000). */
export const MIN_REASON = 10
export const MIN_BREAKGLASS_REASON = 20
export const MAX_REASON = 2000

export function reasonProblem(reason: string, minimum: number = MIN_REASON): string | null {
  const length = reason.trim().length
  if (length < minimum) return `Write at least ${minimum} characters (${length} so far).`
  if (length > MAX_REASON) return `At most ${MAX_REASON} characters.`
  return null
}
